import { USE_OLD_RANGER } from './RcsConstants.js';
/**
 * WebSocket-based request batcher for ranger proxy.
 * Uses persistent WebSocket connections for range requests.
 * Handles partial responses and out-of-order delivery.
 *
 * Used by both RcsDecodeWorker (bulk geometry fetches) and the main-thread
 * WebSocketRcsIO (initial header/structure fetches), each with their own
 * instance, to route reads through the same WebSocket proxy.
 */
export class WebSocketBatcher {
  /**
   * @param {string} proxyUrl - Base URL of the ranger proxy server
   * @param {number} batchSize - Maximum number of requests to group into a single WebSocket message
   * @param {number} timeoutMs - Delay in milliseconds before flushing an incomplete batch
   * @param {number} bytesThreshold - Total byte count that triggers an early batch flush
   * @param {boolean} useRemote - Whether to use the remote ranger proxy instead of the local one
   */
  constructor(proxyUrl, batchSize, timeoutMs, bytesThreshold, useRemote) {
    this.proxyUrl = proxyUrl;
    this.batchSize = batchSize;
    this.timeoutMs = timeoutMs;
    this.bytesThreshold = bytesThreshold;
    this.useRemote = useRemote;

    // WebSocket connections, keyed by URN (new remote ranger) or URL path (old ranger / local proxy).
    this.connections = new Map();

    // Pending requests grouped by batch key before sending.
    // In new remote ranger mode the key is the URN; otherwise it is the resource URL.
    this.pendingRequests = new Map();
    this.batchTimers = new Map();
    this.batchBytes = new Map();

    // Response timeout for WebSocket responses (longer than batch timeout)
    this.responseTimeoutMs = 60_000;
  }

  /**
   * Returns the key used to identify a WebSocket connection and to group batches.
   * In new ranger mode (`!USE_OLD_RANGER`) each URN gets its own authenticated connection,
   * regardless of whether the proxy is local or remote.
   * In old ranger mode connections are per resource-URL path so that different files
   * each get their own socket.
   * @param {string|null} url - Resource URL (used in old ranger mode only)
   * @param {string} urn - Model URN (used in new ranger mode)
   * @returns {string}
   */
  _getConnectionKey(url, urn) {
    if (!USE_OLD_RANGER) {
      return urn;
    }
    return this.getResourcePath(url);
  }

  /**
   * Add a range request to the batch.
   * @param {string|null} url - Resource URL; ignored in new remote ranger mode where the URN identifies the resource
   * @param {number} offset - Byte offset within the resource
   * @param {number} length - Number of bytes to read
   * @param {string} urn    - URN to authenticate this request with on the WebSocket
   * @param {string} token  - Bearer token for the WebSocket auth header
   */
  async fetchRange(url, offset, length, urn, token) {
    const batchKey = this._getConnectionKey(url, urn);
    return new Promise((resolve, reject) => {
      if (!this.pendingRequests.has(batchKey)) {
        this.pendingRequests.set(batchKey, []);
        this.batchBytes.set(batchKey, 0);
      }

      const requests = this.pendingRequests.get(batchKey);
      // url is stored per-request so sendWebSocketRequest can use it for old-path WS URL construction
      requests.push({ url, offset, length, urn, token, resolve, reject });

      // Track total bytes in this batch
      const currentBytes = this.batchBytes.get(batchKey);
      const newBytes = currentBytes + length;
      this.batchBytes.set(batchKey, newBytes);

      // Start timeout if this is the first request for this batch key
      if (requests.length === 1) {
        this.batchTimers.set(batchKey, setTimeout(() => {
          this.flushBatch(batchKey);
        }, this.timeoutMs));
      }

      // Flush immediately if we reached batch size
      if (requests.length >= this.batchSize) {
        this.flushBatch(batchKey);
        return;
      }

      // Flush immediately if total bytes exceeds threshold
      if (newBytes >= this.bytesThreshold) {
        this.flushBatch(batchKey);
        return;
      }
    });
  }

  /**
   * Flush all pending requests for a batch key over WebSocket.
   * @param {string} batchKey - Batch key identifying the group of requests to flush
   */
  async flushBatch(batchKey) {
    const requests = this.pendingRequests.get(batchKey);
    if (!requests || requests.length === 0) {
      return;
    }

    // Clear timeout
    const timer = this.batchTimers.get(batchKey);
    if (timer) {
      clearTimeout(timer);
      this.batchTimers.delete(batchKey);
    }

    // Take all pending requests and reset byte counter
    this.pendingRequests.set(batchKey, []);
    this.batchBytes.set(batchKey, 0);

    try {
      await this.sendWebSocketRequest(requests);
    } catch (error) {
      // Reject all requests in this batch
      for (const req of requests) {
        req.reject(error);
      }
    }
  }

  /**
   * Get resource path from URL (pathname + search). Used in old ranger / local-proxy mode.
   * @param {string} url - Full resource URL
   * @returns {string}
   */
  getResourcePath(url) {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search;
  }

  /**
   * Get or create a WebSocket connection for the given connection key.
   * In new remote ranger mode the key is the URN; in old ranger / local-proxy mode
   * it is the URL path. Authentication messages are sent immediately on open so they
   * are guaranteed to arrive before any subsequent range requests on the same connection.
   * If auth fails the server closes the connection, which rejects all pending requests.
   * @param {string} connectionKey - Map key for the connection (URN or URL path)
   * @param {string|null} url - Resource URL; used to build the WebSocket URL in old ranger / local-proxy mode
   * @param {string} urn - Model URN; sent during auth in new remote ranger mode
   * @param {string} token - Bearer token; sent during auth in new remote ranger mode
   */
  getOrCreateConnection(connectionKey, url, urn, token) {
    if (this.connections.has(connectionKey)) {
      const conn = this.connections.get(connectionKey);
      // Check if WebSocket is still open
      if (conn.ws && conn.ws.readyState === WebSocket.OPEN) {
        return conn;
      }
      // If connecting, return the existing connection (will wait on readyPromise)
      if (conn.ws && conn.ws.readyState === WebSocket.CONNECTING) {
        return conn;
      }
      // Otherwise, close and recreate
      this.closeConnection(connectionKey);
    }

    // Create new connection
    const conn = {
      /** @type {WebSocket|null} */
      ws: null,
      /** @type {Promise<void>|null} */
      readyPromise: null,
      pendingRequests: new Map(), // Map of requestId -> {offset, length, resolve, reject, chunks, timeoutHandle}
      requestIdCounter: 0
    };

    // Build WebSocket URL
    const wsProtocol = this.proxyUrl.startsWith('https') ? 'wss' : 'ws';
    let wsUrl = '';
    if (USE_OLD_RANGER) {
      // Old ranger (local or remote): one connection per resource path
      const path = this.getResourcePath(url);
      if (!this.useRemote) {
        wsUrl = `${wsProtocol}://${new URL(this.proxyUrl).host}/ws${path}`;
      } else {
        const proxyUrl = new URL(this.proxyUrl);
        wsUrl = `${wsProtocol}://${proxyUrl.host}${proxyUrl.pathname}/ws${path}`;
      }
    } else {
      // New ranger (local or remote): one connection per URN; the resource is identified
      // by auth messages, not by the WebSocket endpoint URL.
      if (!this.useRemote) {
        wsUrl = `${wsProtocol}://${new URL(this.proxyUrl).host}/ws`;
      } else {
        const proxyUrl = new URL(this.proxyUrl);
        wsUrl = `${wsProtocol}://${proxyUrl.host}${proxyUrl.pathname}ws`;
      }
    }

    conn.readyPromise = new Promise((resolve, reject) => {
      try {
        conn.ws = new WebSocket(wsUrl);
        conn.ws.binaryType = 'arraybuffer';

        conn.ws.onopen = () => {
          // Auth messages are sent first so the server processes them before
          // any range requests that follow on this same connection.
          // The token comes from loadContext.headers.Authorization in RcsFileLoader
          // and the URN from the model being loaded.
          if (!USE_OLD_RANGER) {
            conn.ws.send(`/headers/${JSON.stringify({ Authorization: `Bearer ${token}` })}`); // gitleaks:allow  - false positive, no secret in code
            conn.ws.send(`/options/{"batch_responses":true,"report_errors":true}`);
            conn.ws.send(`/auth/${urn}`);
          }
          console.log(`WebSocket connected: ${connectionKey}`);
          resolve();
        };

        conn.ws.onclose = (event) => {
          console.log(`WebSocket closed: ${connectionKey}, code=${event.code}, reason=${event.reason}`);
          this.handleConnectionClose(connectionKey, new Error(`WebSocket closed: ${event.code} ${event.reason}`));
        };

        conn.ws.onerror = (error) => {
          console.error(`WebSocket error: ${connectionKey}`, error);
          reject(new Error('WebSocket connection failed'));
        };

        conn.ws.onmessage = (event) => {
          this.handleMessage(connectionKey, event.data);
        };
      } catch (error) {
        reject(error);
      }
    });

    this.connections.set(connectionKey, conn);
    return conn;
  }

  /**
   * Close a WebSocket connection and reject all its in-flight range requests.
   * @param {string} connectionKey - Connection key identifying the connection to close
   */
  closeConnection(connectionKey) {
    const conn = this.connections.get(connectionKey);
    if (conn) {
      if (conn.ws) {
        conn.ws.onclose = null; // Prevent triggering handleConnectionClose
        conn.ws.onerror = null;
        conn.ws.onmessage = null;
        if (conn.ws.readyState === WebSocket.OPEN || conn.ws.readyState === WebSocket.CONNECTING) {
          conn.ws.close();
        }
      }
      // Reject all in-flight requests and clear their timeouts
      const error = new Error('WebSocket connection closed');
      for (const [, req] of conn.pendingRequests) {
        if (req.timeoutHandle) {
          clearTimeout(req.timeoutHandle);
        }
        req.reject(error);
      }
      conn.pendingRequests.clear();
      this.connections.delete(connectionKey);
    }
  }

  /**
   * Handle WebSocket connection close/error by rejecting all pending requests.
   * @param {string} connectionKey - Connection key of the closed connection
   * @param {Error} error - Reason the connection was closed
   */
  handleConnectionClose(connectionKey, error) {
    const conn = this.connections.get(connectionKey);
    if (conn) {
      for (const [, req] of conn.pendingRequests) {
        if (req.timeoutHandle) {
          clearTimeout(req.timeoutHandle);
        }
        req.reject(error);
      }
      conn.pendingRequests.clear();
    }
    this.connections.delete(connectionKey);
  }

  /**
   * Send a batch of range requests over WebSocket. Auth has already been sent
   * during connection setup so only the range message is sent here.
   * @param {Array<{url: string|null, offset: number, length: number, urn: string, token: string, resolve: Function, reject: Function}>} requests - Batch of pending range requests to send
   */
  async sendWebSocketRequest(requests) {
    const { url, urn, token } = requests[0];
    if (!urn || !token) {
      throw new Error('WebSocketBatcher.sendWebSocketRequest: URN and token are required');
    }

    const connectionKey = this._getConnectionKey(url, urn);
    const conn = this.getOrCreateConnection(connectionKey, url, urn, token);

    // Wait for connection to be ready (WebSocket open + auth sent)
    await conn.readyPromise;

    // Check if connection is still valid
    if (!conn.ws || conn.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    // Register pending requests and prepare ranges
    const ranges = [];
    const registeredIds = [];
    for (const req of requests) {
      // Zero-length requests have no bytes to fetch: resolve them directly with an
      // empty result instead of registering and submitting them to the server.
      if (req.length === 0) {
        req.resolve(new Uint8Array(0));
        continue;
      }

      const requestId = conn.requestIdCounter++;
      const pendingReq = {
        offset: req.offset,
        length: req.length,
        resolve: req.resolve,
        reject: req.reject,
        /** @type {Array<{status: number, rangeStart: number, rangeEnd: number, data: Uint8Array}>} */
        chunks: [],
        /** @type {ReturnType<typeof setTimeout>|null} */
        timeoutHandle: null
      };

      // Set timeout for this request
      pendingReq.timeoutHandle = setTimeout(() => {
        this.handleRequestTimeout(connectionKey, requestId);
      }, this.responseTimeoutMs);

      conn.pendingRequests.set(requestId, pendingReq);
      registeredIds.push(requestId);

      // Ranges are [start, end] exclusive
      ranges.push([req.offset, req.offset + req.length]);
    }

    // All requests were zero-length and already resolved; nothing to send.
    if (ranges.length === 0) {
      return;
    }

    // Send JSON message with ranges. If send throws synchronously, clean up the
    // just-registered entries from conn.pendingRequests before re-throwing so that
    // flushBatch's catch can reject the promises without leaving stale entries.
    try {
      if (USE_OLD_RANGER) {
        conn.ws.send(JSON.stringify(ranges));
      } else {
        // New ranger: binary range-get message
        const headerSz = 9;
        const rangeSz = 16;
        const message = new Uint8Array(headerSz + rangeSz * ranges.length);
        const view = new DataView(message.buffer);
        view.setUint8(0, 0x72); // 'r' — range get
        view.setUint32(1, 0, true); // resource index = 0
        view.setUint32(5, ranges.length * rangeSz, true); // data length
        for (let i = 0; i < ranges.length; i++) {
          const off = headerSz + i * rangeSz;
          view.setBigUint64(off, BigInt(ranges[i][0]), true);
          view.setBigUint64(off + 8, BigInt(ranges[i][1]), true);
        }

        conn.ws.send(message);
      }
    } catch (error) {
      for (const id of registeredIds) {
        const req = conn.pendingRequests.get(id);
        if (req?.timeoutHandle) clearTimeout(req.timeoutHandle);
        conn.pendingRequests.delete(id);
      }
      throw error;
    }
  }

  /**
   * Handle request timeout
   * @param {string} connectionKey - Connection key of the connection that timed out
   * @param {number} requestId - ID of the individual pending request that timed out
   */
  handleRequestTimeout(connectionKey, requestId) {
    const conn = this.connections.get(connectionKey);
    if (!conn) return;

    const req = conn.pendingRequests.get(requestId);
    if (req) {
      conn.pendingRequests.delete(requestId);
      req.reject(new Error('WebSocket request timeout'));
    }
  }

  /**
   * Handle incoming WebSocket binary message
   * @param {string} connectionKey - Connection key of the connection that received the message
   * @param {ArrayBuffer} data - Raw binary payload from the WebSocket message event
   */
  handleMessage(connectionKey, data) {
    const conn = this.connections.get(connectionKey);
    if (!conn) return;

    // Parse binary response — returns array of pages (one OPK1 frame may contain many)
    const chunks = this.parseBinaryMessage(data);
    if (!chunks) return;

    for (const chunk of chunks) {
      // Find all pending requests that overlap with this chunk
      for (const [requestId, req] of conn.pendingRequests) {
        const requestStart = req.offset;
        const requestEnd = req.offset + req.length - 1;

        // Check if chunk overlaps with this request
        if (chunk.rangeStart <= requestEnd && chunk.rangeEnd >= requestStart) {
          req.chunks.push(chunk);

          // Check if request is complete
          if (this.isRequestComplete(req)) {
            // Clear timeout
            if (req.timeoutHandle) {
              clearTimeout(req.timeoutHandle);
            }

            // Remove from pending
            conn.pendingRequests.delete(requestId);

            // Reassemble and resolve
            try {
              const result = this.reassembleResponse(req);
              if (result.status === 200 || result.status === 206) {
                if (result.data.byteLength !== req.length) {
                  req.reject(new Error(`Expected ${req.length} bytes, got ${result.data.byteLength}`));
                } else {
                  req.resolve(new Uint8Array(result.data));
                }
              } else {
                req.reject(new Error(`HTTP error: ${result.status}`));
              }
            } catch (error) {
              req.reject(error);
            }
          }
        }
      }
    }
  }

  /**
   * Parse a WebSocket binary response.
   * Format is selected by {@link USE_OLD_RANGER}.
   * @returns {Array<{rangeStart: number, rangeEnd: number, data: Uint8Array, status: number}>|null}
   */
  parseBinaryMessage(data) {
    if (!(data instanceof ArrayBuffer)) {
      console.warn('Received non-binary WebSocket message');
      return null;
    }

    const view = new DataView(data);
    if (USE_OLD_RANGER) {
      return this.parseLegacyBinaryMessage(data, view);
    }

    return this.parseOpk1BinaryMessage(data, view);
  }

  /**
   * Parse an OPK1 binary frame from WebSocket.
   *
   * Frame header (13 bytes):
   *   [0..3]  char[4] magic   = 'OPK1' (0x4f504b31)
   *   [4]     char    type    = 'r'    (range get)
   *   [5..8]  u32 LE  resource index  = 0
   *   [9..12] u32 LE  total body length
   *
   * Body: repeated pages until body exhausted
   *   [+0..+7]  u64 LE  range start
   *   [+8..+15] u64 LE  range end
   *   [+16..]   u8[]    raw data  (length = range end − range start)
   * @param {ArrayBuffer} data
   * @param {DataView} view
   * @returns {Array<{rangeStart: number, rangeEnd: number, data: Uint8Array, status: number}>|null}
   */
  parseOpk1BinaryMessage(data, view) {
    const FRAME_HEADER_LEN = 13;
    const PAGE_HEADER_LEN = 16;

    if (data.byteLength < FRAME_HEADER_LEN) return null;

    // Validate resource type 'r'
    if (view.getUint8(4) !== 0x72) return null;

    const totalChunkLen = view.getUint32(9, true);
    if (data.byteLength < FRAME_HEADER_LEN + totalChunkLen) return null;

    const chunks = [];
    let offset = FRAME_HEADER_LEN;
    const bodyEnd = FRAME_HEADER_LEN + totalChunkLen;

    while (offset + PAGE_HEADER_LEN <= bodyEnd) {
      const rangeStart = Number(view.getBigUint64(offset, true));
      const rangeEnd = Number(view.getBigUint64(offset + 8, true));
      if (rangeEnd <= rangeStart) return null;

      const payloadLen = rangeEnd - rangeStart;
      offset += PAGE_HEADER_LEN;

      if (offset + payloadLen > bodyEnd) return null;

      chunks.push({
        rangeStart,
        rangeEnd,
        data: new Uint8Array(data, offset, payloadLen),
        status: 200
      });
      offset += payloadLen;
    }

    return chunks.length > 0 ? chunks : null;
  }

  /**
   * Parse a legacy ranger WebSocket binary message.
   * Format: 2 bytes status, 4 bytes length, 8 bytes start, 8 bytes end, data
   * @param {ArrayBuffer} data
   * @param {DataView} view
   * @returns {Array<{rangeStart: number, rangeEnd: number, data: Uint8Array, status: number}>|null}
   */
  parseLegacyBinaryMessage(data, view) {
    // Header: 2 bytes status + 4 bytes length + 8 bytes start + 8 bytes end
    const HEADER_LEN = 22;
    if (data.byteLength < HEADER_LEN) return null;

    let offset = 0;

    // Read status (2 bytes, little-endian)
    const status = view.getUint16(offset, true);
    offset += 2;

    // Skip data length (4 bytes) — not used; actual length is derived from rangeEnd - rangeStart
    offset += 4;

    // Read range start (8 bytes, little-endian)
    const rangeStartLow = view.getUint32(offset, true);
    offset += 4;
    const rangeStartHigh = view.getUint32(offset, true);
    offset += 4;
    const rangeStart = rangeStartHigh * 0x100000000 + rangeStartLow;

    // Read range end (8 bytes, little-endian)
    const rangeEndLow = view.getUint32(offset, true);
    offset += 4;
    const rangeEndHigh = view.getUint32(offset, true);
    offset += 4;
    const rangeEnd = rangeEndHigh * 0x100000000 + rangeEndLow;

    const dataLength = rangeEnd - rangeStart;

    // Read data
    const chunkData = new Uint8Array(data.slice(offset, offset + dataLength));

    return [{
      status,
      rangeStart,
      rangeEnd,
      data: chunkData
    }];
  }

  /**
   * Check if a request has received all its data
   * @param {{offset: number, length: number, chunks: Array<{rangeStart: number, rangeEnd: number, status: number, data: Uint8Array}>}} req - Pending request to check for completeness
   * @returns {boolean}
   */
  isRequestComplete(req) {
    const requestStart = req.offset;
    const requestEnd = req.offset + req.length;

    // Sort chunks by range start
    const sortedChunks = [...req.chunks].sort((a, b) => a.rangeStart - b.rangeStart);

    // Check for complete coverage (all boundaries are exclusive)
    let coveredUpTo = requestStart;
    for (const chunk of sortedChunks) {
      if (chunk.rangeStart > coveredUpTo) {
        return false;
      }
      coveredUpTo = Math.max(coveredUpTo, chunk.rangeEnd);
      if (coveredUpTo >= requestEnd) {
        return true;
      }
    }

    return false;
  }

  /**
   * Reassemble response data from chunks
   * Similar logic to parseRangerBinResponse
   * @param {{offset: number, length: number, chunks: Array<{rangeStart: number, rangeEnd: number, status: number, data: Uint8Array}>}} req - Completed pending request whose chunks should be assembled into the final response
   */
  reassembleResponse(req) {
    const requestStart = req.offset;

    // Check for error status in any chunk
    const errorChunk = req.chunks.find((c) => c.status !== 200 && c.status !== 206);
    if (errorChunk) {
      return {
        status: errorChunk.status,
        data: new ArrayBuffer(0)
      };
    }

    // Sort chunks by range start
    const sortedChunks = [...req.chunks].sort((a, b) => a.rangeStart - b.rangeStart);

    if (sortedChunks.length === 1) {
      // Single chunk case
      const chunk = sortedChunks[0];
      const chunkOffset = requestStart - chunk.rangeStart;
      const data = chunk.data.slice(chunkOffset, chunkOffset + req.length);
      return {
        status: chunk.status,
        data: data.buffer
      };
    }

    // Multiple chunks - reassemble
    const resultData = new Uint8Array(req.length);
    for (const chunk of sortedChunks) {
      let dstOffset = chunk.rangeStart - requestStart;
      let srcOffset = 0;

      // If the chunk starts before the request, we need to copy the overlapping part
      if (dstOffset < 0) {
        srcOffset = -dstOffset;
        dstOffset = 0;
      }

      const copyLength = Math.min(chunk.data.length - srcOffset, req.length - dstOffset);

      // Chunk starts at or after request start (subarray is OK, as set() copies from it anyway).
      resultData.set(chunk.data.subarray(srcOffset, srcOffset + copyLength), dstOffset);
    }

    return {
      status: 206,
      data: resultData.buffer
    };
  }
}